import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { EmptyState } from "../../components/Feedback";
import { EtapeIcon, fmtDate, eur, PHASE_STATUS, validatedByLabel } from "../../lib/helpers";
import Card from "../../components/Card";
import HeadStat from "../../components/HeadStat";
import RdvEditor from "../../components/RdvEditor";
import FormEditor from "../../components/FormEditor";
import ProjectBilling from "../../components/ProjectBilling";
import KpiSummary from "../../components/KpiSummary";
import RecordShell from "../../components/RecordShell";
import ContactButton from "../../components/ContactButton";
import { toast } from "sonner";
import AuthImg from "../../components/AuthImg";
import Switch from "../../components/Switch";
import AuthFileLink from "../../components/AuthFileLink";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";
import Conversation, { agencyAuthor } from "../../components/Conversation";
import { ASSISTANT_ID } from "../../components/AssistantAvatar";

const STATUS_OPTIONS = [
  ["non_demarre", "À venir"], ["en_cours", "En cours"],
  ["en_attente_client", "En attente de validation client"],
  ["en_correction", "En cours de correction"],
  ["en_attente_agence", "En attente de validation agence"],
  ["valide_entreprise", "Validée par l'entreprise"], ["valide_agence", "Validée par Metara"],
  // « Livré » retiré le 28/09 (lot 4, Q5) : septième statut hors modèle, désormais refusé par le serveur.
];

const ProjectLinks = ({ project, onSave }) => {
  const [links, setLinks] = useState(() => (project.links || []).map((l) => ({ ...l })));
  const [saving, setSaving] = useState(false);
  const orig = JSON.stringify(project.links || []);
  const dirty = JSON.stringify(links) !== orig;

  const addLink = () => setLinks([...links, { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), name: "", description: "", url: "", visible: false }]);
  const update = (id, patch) => setLinks(links.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const remove = (id) => setLinks(links.filter((l) => l.id !== id));

  const save = async () => {
    const clean = links.filter((l) => (l.name || "").trim() || (l.url || "").trim());
    for (const l of clean) {
      if (!l.name?.trim()) return toast.error("Chaque lien doit avoir un nom");
      if (!l.url?.trim()) return toast.error(`Renseignez l'URL du lien « ${l.name} »`);
    }
    setSaving(true);
    try { await onSave(clean); setLinks(clean.map((l) => ({ ...l }))); toast.success("Liens enregistrés"); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><h2 className="font-display text-xl text-marine">Liens externes</h2></div>
        <button onClick={addLink} data-testid="add-link-btn" className="text-sm border border-marine/30 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Plus size={16} /> Ajouter un lien</button>
      </div>
      <p className="text-xs text-marine/80 mb-4">Ajoutez autant de liens que nécessaire (Metricool, Canva, Figma, Drive…). Pour chacun : un nom, une description et une visibilité individuelle par l'entreprise cliente.</p>
      <div className="space-y-4">
        {links.length === 0 && <p className="text-sm text-marine/80">Aucun lien. Cliquez sur « Ajouter un lien ».</p>}
        {links.map((l, i) => (
          <div key={l.id} className="border border-marine/10 rounded-sm p-4" data-testid={`link-row-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <input value={l.name} onChange={(e) => update(l.id, { name: e.target.value })} data-testid={`link-name-${i}`}
                placeholder="Nom du lien (ex. Calendrier Metricool)" className="flex-1 border border-marine/20 rounded-sm px-3 py-2 text-sm font-medium text-marine outline-none focus:ring-1 focus:ring-marine" />
              <button onClick={() => remove(l.id)} data-testid={`link-delete-${i}`} className="text-rouge/70 hover:text-rouge p-2" title="Supprimer ce lien"><Icons.Trash2 size={16} /></button>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input value={l.url} onChange={(e) => update(l.id, { url: e.target.value })} data-testid={`link-url-${i}`}
                placeholder="https://..." className="flex-1 border border-marine/20 rounded-sm px-3 py-2 text-sm text-marine outline-none focus:ring-1 focus:ring-marine" />
              {l.url && <a href={l.url} target="_blank" rel="noreferrer" data-testid={`link-open-${i}`} className="text-marine hover:text-bordeaux p-2" title="Ouvrir"><Icons.ExternalLink size={16} /></a>}
            </div>
            <textarea value={l.description} onChange={(e) => update(l.id, { description: e.target.value })} data-testid={`link-desc-${i}`}
              rows={2} placeholder="Description (ex. Le calendrier éditorial est consulté sur Metricool)" className="w-full border border-marine/20 rounded-sm px-3 py-2 text-sm text-marine outline-none focus:ring-1 focus:ring-marine" />
            <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
              <Switch checked={!!l.visible} onChange={() => update(l.id, { visible: !l.visible })} testid={`link-visible-${i}`} title="Visible par l'entreprise cliente" />
              <span className="text-sm text-marine/80 flex items-center gap-1">{l.visible ? <Icons.Eye size={16} className="text-marine" /> : <Icons.EyeOff size={16} className="text-marine" />} Visible par l'entreprise cliente</span>
            </label>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={!dirty || saving} data-testid="save-links-btn"
          className={`rounded-sm px-5 py-2 text-sm text-white transition-colors ${dirty && !saving ? "bg-marine hover:bg-marine-hover" : "bg-sable-fonce cursor-not-allowed"}`}>Enregistrer les liens</button>
      </div>
    </Card>
  );
};

export default function AdminProjectDetail() {
  const { confirm } = useConfirm();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [clients, setClients] = useState([]);
  const [addClient, setAddClient] = useState("");
  const [addMember, setAddMember] = useState("");
  const [rythme, setRythme] = useState("");
  const fileRef = useRef();
  const uploadKindRef = useRef("metara");
  // Fiche supprimée ou adresse erronée (lien d'un e-mail, d'une notification) : un message et un retour, plus une page
  // vide (recette du 15/10, ANO-04, étendu aux fiches de l'agence sur accord de Mélilia).
  const load = useCallback(() => api.get(`/admin/projects/${id}`).then((r) => { setP(r.data); setRythme(r.data.social_volume ?? ""); setLoadError(null); })
    .catch((e) => setLoadError([403, 404].includes(e?.response?.status) ? "missing" : "failed")), [id]);
  useEffect(() => { load(); api.get("/admin/collaborators").then((r) => setCollabs(r.data)); api.get("/admin/clients").then((r) => setClients(r.data)); }, [id, load]);
  if (!p && loadError) return (
    <EmptyState icon="FolderX" title={loadError === "missing" ? "Projet introuvable" : "Impossible d'afficher cette fiche"}
      hint={loadError === "missing" ? "Il a peut-être été supprimé." : "Vérifiez votre connexion, puis rechargez la page."}
      action={<button onClick={() => nav("/admin/projets")} className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Retour aux projets</button>} />
  );
  if (!p) return null;

  const team = p.team || [];
  const saveTeam = async (next) => { await api.put(`/admin/projects/${id}`, { team: next }); load(); };
  const saveRythme = async () => {
    try { await api.put(`/admin/projects/${id}`, { social_volume: rythme === "" ? null : rythme }); toast.success("Rythme du mois enregistré"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };
  const assignMember = async () => {
    if (!addMember) return;
    const c = collabs.find((x) => x.id === addMember);
    if (!c || team.some((t) => t.id === c.id)) { setAddMember(""); return; }
    await saveTeam([...team, { id: c.id, name: c.name, job_role: c.job_role || "", client_visible: false }]);
    setAddMember(""); toast.success("Membre assigné");
  };
  const removeMember = async (mid) => { await saveTeam(team.filter((t) => t.id !== mid)); };
  const toggleMemberVisible = async (mid) => { await saveTeam(team.map((t) => (t.id === mid ? { ...t, client_visible: !t.client_visible } : t))); };
  const sharedIds = p ? (p.shared_client_ids || []) : [];
  const saveShared = async (next) => { await api.put(`/admin/projects/${id}`, { shared_client_ids: next }); load(); };
  const addSharedClient = async () => {
    if (!addClient || addClient === p.client_id || sharedIds.includes(addClient)) { setAddClient(""); return; }
    await saveShared([...sharedIds, addClient]); setAddClient(""); toast.success("Entreprise ajoutée au projet");
  };
  const removeSharedClient = async (cid) => { await saveShared(sharedIds.filter((x) => x !== cid)); toast.success("Accès retiré"); };
  const setStatus = async (key, val) => {
    let body;
    if (val === "valide_entreprise") body = { status: "valide", validated_by_type: "entreprise" };
    else if (val === "valide_agence") body = { status: "valide", validated_by_type: "agence" };
    else body = { status: val };
    // Erreur affichée (30/09) : refus du serveur, ou 409 « modifié ailleurs » — la fiche est rechargée dans tous les cas.
    try { await api.put(`/admin/projects/${id}/etapes/${key}`, body); toast.success("Statut mis à jour"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Changement de statut impossible"); }
    load();
  };
  const setDate = async (key, planned_date) => {
    try { await api.put(`/admin/projects/${id}/etapes/${key}`, { planned_date }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement"); }
    load();
  };
  const setPhaseStatus = async (phaseKey, val) => {
    try { await api.put(`/admin/projects/${id}/phases/${phaseKey}/state`, { status: val }); toast.success("Statut de phase mis à jour"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Changement de statut impossible"); }
  };
  // L'éditeur appelant affiche l'erreur ; la fiche est rechargée même en cas d'échec (30/09).
  const saveVideo = async (key, body) => { try { await api.put(`/admin/projects/${id}/etapes/${key}`, body); } finally { await load(); } };
  const resetForm = async (key) => { try { await api.post(`/admin/projects/${id}/etapes/${key}/form/reset`); } finally { await load(); } };
  const uploadDel = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); fd.append("kind", uploadKindRef.current); try { await api.post(`/admin/projects/${id}/deliverables`, fd); toast.success(uploadKindRef.current === "metara" ? "Fichier METARA ajouté" : "Document source ajouté"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); } finally { if (fileRef.current) fileRef.current.value = ""; } };
  const triggerUpload = (kind) => { uploadKindRef.current = kind; fileRef.current?.click(); };
  const deleteDel = async (did, name) => { if (!(await confirm({ title: `Supprimer « ${name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; try { await api.delete(`/admin/projects/${id}/deliverables/${did}`); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); } };
  // Conversation du projet (01/10) : pastille d'étape facultative ; « Marquer comme traité » retire un message de « À traiter ».
  const sendMessage = async (text, etapeKey) => { await api.post(`/admin/projects/${id}/comments`, { text, etape_key: etapeKey }); await load(); };
  const markHandled = async (cid) => {
    try { await api.post(`/admin/projects/${id}/comments/${cid}/handled`); toast.success("Marqué comme traité"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    load();
  };
  const toggleDraft = async () => { await api.put(`/admin/projects/${id}`, { draft: !p.draft }); toast.success(!p.draft ? "Projet passé en brouillon" : "Projet publié"); load(); };
  const deleteProject = async () => { if (!(await confirm({ title: `Supprimer définitivement le projet « ${p.name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; await api.delete(`/admin/projects/${id}`); toast.success("Projet supprimé"); nav("/admin/projets"); };

  const fin = p.financials || {};
  const ni = fin.next_installment;

  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux">{p.type_label}</span>
            {p.draft && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-sable border border-marine/30 text-gris-texte">Brouillon</span>}
          </div>
          <h1 className="font-display text-2xl text-marine">{p.name}</h1>
          <div className="text-sm text-marine/80 flex items-center gap-3 flex-wrap mt-0.5">
            {p.client_name && <Link to={`/admin/clients/${p.client_id}`} className="hover:text-marine">{p.client_name}</Link>}
            <span>{p.progress ?? 0} % d'avancement</span>
          </div>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:shrink-0">
          <ContactButton contextType="project" contextId={id} contextLabel={p.name}
            recipients={[
              ...(p.client_id ? [{ id: p.client_id, name: p.client_name || "Entreprise", role: "client" }] : []),
              ...(p.shared_clients || []).map((sc) => ({ id: sc.id, name: sc.name, role: "client" })),
              ...team.map((t) => ({ id: t.id, name: t.name, role: "collaborator" })),
            ]} testid="project-contact" />
          <button onClick={toggleDraft} data-testid="project-draft-toggle" className="text-sm border border-marine/30 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1">{p.draft ? <><Icons.Eye size={16} /> Publier</> : <><Icons.EyeOff size={16} /> Brouillon</>}</button>
          <button onClick={deleteProject} data-testid="project-delete" className="text-sm border border-rouge/30 text-rouge-texte rounded-sm px-3 py-1.5 hover:bg-rouge hover:text-white transition-colors flex items-center gap-1"><Icons.Trash2 size={16} /> Supprimer</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-y-3 [&>*:last-child:nth-child(odd)]:col-span-2 mt-4 pt-4 border-t border-marine/10 sm:flex sm:items-stretch sm:gap-1 sm:overflow-x-auto no-scrollbar sm:divide-x sm:divide-marine/10">
        <HeadStat label="Avancement" value={`${p.progress ?? 0}%`} />
        <HeadStat label="Total" value={eur(fin.total, p.currency)} />
        <HeadStat label="Encaissé" value={eur(fin.encaisse, p.currency)} accent="text-bleu" />
        <HeadStat label="Reste" value={eur(fin.reste_a_encaisser, p.currency)} accent="text-bordeaux" />
        <HeadStat label="Missions" value={String((p.missions || []).length)} />
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <KpiSummary testid="project-kpis" items={[
        { testid: "kpi-progress", label: "Avancement", value: `${p.progress ?? 0} %` },
        { testid: "kpi-total", label: "Total du projet", value: eur(fin.total, p.currency) },
        { testid: "kpi-encaisse", accent: "text-bleu", label: "Déjà encaissé", value: eur(fin.encaisse, p.currency) },
        { testid: "kpi-reste", accent: "text-bordeaux", label: "Reste à encaisser", value: eur(fin.reste_a_encaisser, p.currency) },
        { testid: "kpi-next", label: "Prochaine échéance", value: ni ? eur(ni.amount, p.currency) : "—", sub: ni ? fmtDate(ni.due_date) : "Aucune échéance restante" },
        { testid: "kpi-missions", label: "Missions", value: String((p.missions || []).length) },
      ]} />
      {p.type === "reseaux_sociaux" && (
        <Card>
          <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Rythme du mois</h2></div>
          <p className="text-sm text-marine/80 mb-3">Nombre de publications convenu pour ce mois. Laissez vide pour appliquer le rythme du contrat. Un mois allégé à la demande de l'entreprise se rattrape sur le suivant : c'est ce chiffre qui en garde la trace.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">Publications ce mois-ci
              <input type="number" min="0" value={rythme} data-testid="project-rythme"
                onChange={(e) => setRythme(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0))}
                className="inp mt-1 w-40" placeholder="Rythme du contrat" />
            </label>
            <button onClick={saveRythme} data-testid="project-rythme-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Enregistrer</button>
          </div>
        </Card>
      )}
      <Card>
        <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Accès entreprises</h2></div>
        <p className="text-sm text-marine/80 mb-3">L'entreprise propriétaire et, éventuellement, d'autres entreprises de l'agence peuvent accéder à ce projet commun.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="flex items-center gap-1.5 border border-marine/20 bg-creme rounded-full pl-1.5 pr-2.5 py-1 text-sm" data-testid="project-owner-client">
            <span className="w-6 h-6 rounded-full bg-marine text-white flex items-center justify-center text-xs font-display">{p.client_name?.[0]?.toUpperCase()}</span>
            {p.client_name} <span className="text-[10px] uppercase tracking-wider text-bordeaux">Propriétaire</span>
          </span>
          {(p.shared_clients || []).map((sc) => (
            <span key={sc.id} data-testid={`project-shared-${sc.id}`} className="flex items-center gap-1.5 border border-marine/10 rounded-full pl-1.5 pr-2 py-1 text-sm">
              <span className="w-6 h-6 rounded-full bg-bordeaux text-white flex items-center justify-center text-xs font-display">{sc.name?.[0]?.toUpperCase()}</span>
              {sc.name}
              <button onClick={() => removeSharedClient(sc.id)} data-testid={`project-shared-remove-${sc.id}`} aria-label={`Retirer ${sc.name || "cette entreprise"}`} title="Retirer" className="text-rouge hover:opacity-70"><Icons.X size={16} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-md">
          <select aria-label="Entreprise à rattacher au projet" value={addClient} onChange={(e) => setAddClient(e.target.value)} data-testid="project-shared-select" className="inp">
            <option value="">— Ajouter une entreprise au projet —</option>
            {clients.filter((cl) => cl.id !== p.client_id && !sharedIds.includes(cl.id)).map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
          </select>
          <button onClick={addSharedClient} data-testid="project-shared-add" className="border border-marine text-marine rounded-sm px-4 text-sm hover:bg-marine hover:text-white transition-colors whitespace-nowrap">Ajouter</button>
        </div>
      </Card>
      {p.type === "identite_visuelle" && (
        <Link to={`/admin/identite?client=${p.client_id}`} data-testid="open-identity-cms" className="flex items-center justify-between gap-3 bg-bordeaux text-white rounded-sm px-5 py-4 hover:bg-bordeaux-hover transition-colors">
          <div className="flex items-center gap-3"><Icons.Palette size={16} /><div><div className="text-sm font-medium">Compléter la charte de marque</div><div className="text-xs text-white/80">Logo, couleurs et typographies — CMS Identité Visuelle</div></div></div>
          <Icons.ArrowRight size={16} />
        </Link>
      )}
      <ProjectLinks project={p} onSave={async (links) => { await api.put(`/admin/projects/${id}`, { links }); await load(); }} />
    </div>
  );

  const renderEquipe = () => (
    <Card>
      <div className="flex items-center gap-2 mb-3"><h2 className="font-display text-xl text-marine">Équipe du projet</h2></div>
      <p className="text-xs text-marine/80 mb-4">L'agence Metara apparaît toujours dans l'équipe côté client. Pour chaque collaborateur, choisissez s'il est visible par l'entreprise cliente.</p>

      {p.agency && (
        <div className="flex items-center gap-3 border border-bordeaux/20 bg-bordeaux-50/50 rounded-sm p-3 mb-3" data-testid="project-team-agency">
          <span className="w-9 h-9 rounded-full bg-bordeaux text-white flex items-center justify-center overflow-hidden text-sm font-display shrink-0">
            {p.agency.avatar_url ? <AuthImg path={p.agency.avatar_url} alt={p.agency.name} className="w-full h-full object-cover" /> : p.agency.name?.[0]?.toUpperCase()}
          </span>
          <div className="flex-1">
            <div className="text-sm font-medium text-marine">{p.agency.name}</div>
            <div className="text-xs text-bordeaux">Agence Metara</div>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-bleu bg-bleu-50 border border-bleu/20 rounded-sm px-2 py-1 flex items-center gap-1">Toujours visible</span>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {team.length === 0 && <p className="text-sm text-marine/80">Aucun collaborateur assigné.</p>}
        {team.map((t) => (
          <div key={t.id} data-testid={`project-team-${t.id}`} className="flex items-center gap-3 border border-marine/10 rounded-sm p-3">
            <span className="w-9 h-9 rounded-full bg-marine text-white flex items-center justify-center overflow-hidden text-sm font-display shrink-0">
              {t.avatar_url ? <AuthImg path={t.avatar_url} alt={t.name} className="w-full h-full object-cover" /> : t.name?.[0]?.toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-marine truncate">{t.name}</div>
              {t.job_role && <div className="text-xs text-marine/80">{t.job_role}</div>}
            </div>
            <Switch checked={!!t.client_visible} onChange={() => toggleMemberVisible(t.id)} testid={`team-visible-${t.id}`} title="Visible par le client" />
            <span className="text-xs text-marine/80 w-20 flex items-center gap-1">{t.client_visible ? <><Icons.Eye size={16} className="text-marine" /> Visible</> : <><Icons.EyeOff size={16} className="text-marine" /> Masqué</>}</span>
            <button aria-label={`Retirer ${t.name} de l'équipe`} onClick={() => removeMember(t.id)} data-testid={`remove-team-${t.id}`} className="text-rouge hover:opacity-70 shrink-0"><Icons.X size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 max-w-md">
        <select aria-label="Membre à ajouter à l'équipe" value={addMember} onChange={(e) => setAddMember(e.target.value)} data-testid="project-team-select" className="inp">
          <option value="">— Assigner un collaborateur —</option>
          {collabs.filter((c) => !team.some((t) => t.id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}{c.job_role ? ` (${c.job_role})` : ""}</option>)}
        </select>
        <button onClick={assignMember} data-testid="project-team-add" className="border border-marine text-marine rounded-sm px-4 text-sm hover:bg-marine hover:text-white transition-colors whitespace-nowrap">Assigner</button>
      </div>
    </Card>
  );

  const renderFacturation = () => <ProjectBilling p={p} reload={load} />;

  const renderEtapes = () => (
    <div className="space-y-6">
      {(p.phases || []).map((ph) => (
        <Card key={ph.key}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="font-display text-xl text-marine">{ph.name}</h2>
            <select aria-label={`Statut de la phase ${ph.name}`} value={ph.status} onChange={(e) => setPhaseStatus(ph.key, e.target.value)}
              data-testid={`admin-phase-status-${ph.key}`} className="text-xs border border-marine/20 rounded-sm p-1.5">
              {/* a_venir → cloturee retiré statiquement : seule transition structurellement
                  absurde (rien n'a commencé), indépendante des données. Les deux autres
                  refus possibles (Règles 1 et 2, Lot 3b) dépendent de l'état des étapes au
                  moment du clic — pas dupliqués ici, gérés par le message du backend. */}
              {Object.entries(PHASE_STATUS)
                .filter(([k]) => !(ph.status === "a_venir" && k === "cloturee"))
                .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="space-y-3">
            {(ph.etapes || []).map((et) => (
              <div key={et.key} className="rounded-sm border border-marine/10 p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <EtapeIcon status={et.status} />
                    <span className="font-medium text-marine">{et.name}</span>
                    {et.blocking && <span className="text-[9px] uppercase tracking-wider whitespace-nowrap bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm" title="Les étapes suivantes de cette phase ne démarrent pas tant que celle-ci n'est pas validée">Étape obligatoire</span>}
                  </div>
                  {/* flex-wrap : à 390 px, date + menu de statut (≈ 330 px) dépassaient la carte et faisaient défiler la page de 22 px (constat du 28/09). */}
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <input aria-label={`Date prévue de l'étape ${et.name}`} type="date" value={et.planned_date ? et.planned_date.slice(0, 10) : ""} onChange={(e) => setDate(et.key, new Date(e.target.value).toISOString())} className="text-xs border border-marine/20 rounded-sm p-1.5" />
                    <select aria-label={`Statut de l'étape ${et.name}`} value={et.status === "valide" ? (et.validated_by_type === "entreprise" ? "valide_entreprise" : "valide_agence") : et.status} onChange={(e) => setStatus(et.key, e.target.value)} data-testid={`admin-etape-${et.key}`} className="text-xs border border-marine/20 rounded-sm p-1.5 max-w-full">
                      {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
                {et.validated_at && <div className="text-xs text-marine/80 mt-1">Validée le {fmtDate(et.validated_at)} {validatedByLabel(et, "admin")}</div>}
                {et.description && <div className="text-xs text-marine/80 mt-2 whitespace-pre-line">{et.description}</div>}
                <RdvEditor rdv={et} onSave={(body) => saveVideo(et.key, body)} testId={et.key} />
                <FormEditor form={et.form} onSave={(body) => saveVideo(et.key, body)} onReset={() => resetForm(et.key)} testId={et.key} />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );

  const renderFichiers = () => (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-marine">Fichiers du projet</h2>
        <input ref={fileRef} type="file" className="hidden" onChange={uploadDel} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div data-testid="metara-files-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-bordeaux flex items-center gap-1"><Icons.Share2 size={16} /> Fichiers partagés par METARA</div>
            <button onClick={() => triggerUpload("metara")} data-testid="upload-metara" className="text-xs border border-marine text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Upload size={16} /> Ajouter</button>
          </div>
          <p className="text-[11px] text-marine/80 mb-2">Briefs, logos, chartes, références, livrables — visibles par l'entreprise.</p>
          <div className="space-y-2">
            {p.deliverables.filter((d) => (d.kind || "metara") === "metara").map((d) => (
              <div key={d.id} className="rounded-sm border border-marine/10 p-3 flex items-center justify-between">
                <span className="text-sm flex items-center gap-2 truncate"><Icons.FileText size={16} className="text-marine shrink-0" /> {d.name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <AuthFileLink path={d.path} title="Télécharger" className="p-1.5 text-marine hover:bg-marine-50 rounded-sm inline-flex"><Icons.Download size={16} /></AuthFileLink>
                  <button onClick={() => deleteDel(d.id, d.name)} data-testid={`delete-deliverable-${d.id}`} title="Supprimer" className="p-1.5 text-rouge hover:bg-rouge-50 rounded-sm"><Icons.Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {p.deliverables.filter((d) => (d.kind || "metara") === "metara").length === 0 && <p className="text-sm text-marine/80">Aucun fichier.</p>}
          </div>
        </div>
        <div data-testid="client-sources-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-bleu-fonce flex items-center gap-1"><Icons.FolderInput size={16} /> Documents sources de l'entreprise</div>
            <button onClick={() => triggerUpload("client_source")} data-testid="upload-source" className="text-xs border border-marine text-marine rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1"><Icons.Upload size={16} /> Ajouter</button>
          </div>
          <p className="text-[11px] text-marine/80 mb-2">Vidéos, exports, fichiers bruts transmis par l'entreprise.</p>
          <div className="space-y-2">
            {p.deliverables.filter((d) => d.kind === "client_source").map((d) => (
              <div key={d.id} className="rounded-sm border border-bleu/20 p-3 flex items-center justify-between">
                <span className="text-sm flex items-center gap-2 truncate min-w-0"><Icons.FileVideo size={16} className="text-bleu shrink-0" /> <span className="truncate">{d.name}</span></span>
                <div className="flex items-center gap-1 shrink-0">
                  {d.uploaded_by_name && <span className="text-xs text-marine/80 mr-1 hidden sm:inline">{d.uploaded_by_name}</span>}
                  <AuthFileLink path={d.path} title="Télécharger" className="p-1.5 text-marine hover:bg-marine-50 rounded-sm inline-flex"><Icons.Download size={16} /></AuthFileLink>
                  <button onClick={() => deleteDel(d.id, d.name)} data-testid={`delete-deliverable-${d.id}`} title="Supprimer" className="p-1.5 text-rouge hover:bg-rouge-50 rounded-sm"><Icons.Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {p.deliverables.filter((d) => d.kind === "client_source").length === 0 && <p className="text-sm text-marine/80">Aucun document source.</p>}
          </div>
        </div>
      </div>
    </Card>
  );

  const renderMissions = () => (
    <Card>
      <h2 className="font-display text-xl text-marine mb-4">Missions liées</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {(p.missions || []).length === 0 && <p className="text-sm text-marine/80">Aucune mission rattachée à ce projet.</p>}
        {(p.missions || []).map((m) => (
          <button key={m.id} onClick={() => nav(`/admin/collaborateurs/${m.collaborator_id}`)} data-testid={`linked-mission-${m.id}`} className="text-left rounded-sm border border-marine/10 p-4 hover:border-marine/40 transition-colors flex items-center justify-between">
            <div><div className="font-medium text-marine flex items-center gap-1.5"><Icons.Clapperboard size={16} className="text-bordeaux" /> {m.type}</div><div className="text-xs text-marine/80 mt-0.5">{m.collaborator_name}</div></div>
            <Icons.ArrowRight size={16} className="text-marine" />
          </button>
        ))}
      </div>
    </Card>
  );

  const renderConversation = () => {
    const etapes = (p.phases || []).flatMap((ph) => ph.etapes || []);
    const names = Object.fromEntries(etapes.map((e) => [e.key, e.name]));
    const messages = (p.comments || []).map((c) => ({
      id: c.id, mine: c.author_role === "admin", text: c.text, created_at: c.created_at,
      author: c.author_role === "admin" ? agencyAuthor(c.author_name) : c.author_name,
      assistant: c.author_id === ASSISTANT_ID,
      badge: c.etape_key ? { kind: "Étape", label: names[c.etape_key] || "" } : null,
      kindLabel: c.kind === "modification" ? "Demande de modification" : "",
      awaiting: !!c.awaiting, handled: !!c.handled_at,
    }));
    return (
      <Conversation messages={messages} canHandle onHandle={markHandled} selectLabel="Étape concernée"
        options={etapes.map((e) => ({ value: e.key, label: e.name }))} placeholder="Écrire à l'entreprise…"
        onSend={sendMessage} highlightId={sp.get("message")} testid="conversation" />
    );
  };

  const sections = [
    { id: "overview", label: "Vue d'ensemble", icon: "LayoutDashboard", render: renderOverview },
    { id: "equipe", label: "Équipe", icon: "Users", render: renderEquipe },
    { id: "facturation", label: "Facturation", icon: "Wallet", render: renderFacturation },
    { id: "etapes", label: "Étapes", icon: "ListChecks", render: renderEtapes },
    { id: "fichiers", label: "Fichiers", icon: "FolderOpen", render: renderFichiers },
    { id: "missions", label: "Missions", icon: "Clapperboard", render: renderMissions },
    { id: "conversation", label: "Conversation", icon: "MessagesSquare", render: renderConversation },
  ];

  return (
    <div>
      <button onClick={() => nav("/admin/projets")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Retour</button>
      <RecordShell
        testid="project-record"
        breadcrumb={[
          { label: "Projets", to: "/admin/projets" },
          p.client_name ? { label: p.client_name, to: `/admin/clients/${p.client_id}` } : null,
          // L'étage du module manquait (27/10) : on arrive ici par le menu « Modules », et rien ne
          // ramenait à la page « cette entreprise × ce module » — là où vivent ses comptes à
          // identifier, son calendrier et son contrat. Deux choses s'appellent « Social Media »,
          // le fil d'Ariane doit dire laquelle est laquelle.
          p.client_id && p.type ? { label: p.type_label || p.type, to: `/admin/clients/${p.client_id}/modules/${p.type}` } : null,
          { label: p.name },
        ].filter(Boolean)}
        header={header}
        sections={sections}
      />
    </div>
  );
}
